import React, { useEffect, useRef } from 'react';
import HomeHeader from '@/components/HomeHeader';
import Footer from '@/components/homepage/Footer';
import { usePageMeta } from '@/hooks/usePageMeta';
import TableOfContents from '@/components/resources/TableOfContents';
import EmailCapture from '@/components/resources/EmailCapture';

const TITLE = 'How to Make a Missionary Support-Raising Video (Script + Filming Tips)';
const DESCRIPTION =
    "A short, honest video often out-performs a long letter for getting a first response. A practical script structure, filming tips that don't require any equipment, and where to actually send it.";

const JSON_LD = {
    '@context': 'https://schema.org',
    '@graph': [
        {
            '@type': 'Article',
            headline: TITLE,
            description: DESCRIPTION,
            image: 'https://devotionfund.com/resources/cover24.jpg',
            datePublished: '2026-08-07',
            dateModified: '2026-08-07',
            author: { '@type': 'Organization', name: 'Devotion Fund', url: 'https://devotionfund.com' },
            publisher: {
                '@type': 'Organization',
                name: 'Devotion Fund',
                logo: { '@type': 'ImageObject', url: 'https://devotionfund.com/logo.png' },
            },
            mainEntityOfPage: {
                '@id': 'https://devotionfund.com/resources/how-to-make-a-missionary-support-raising-video',
                '@type': 'WebPage',
            },
        },
        {
            '@type': 'FAQPage',
            mainEntity: [
                {
                    '@type': 'Question',
                    name: 'How long should a missionary support-raising video be?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: "Somewhere between 90 seconds and 3 minutes for a general appeal video. Long enough to explain who you are and what you're doing, short enough that people actually finish it — a video nobody watches to the end does nothing for you.",
                    },
                },
                {
                    '@type': 'Question',
                    name: 'Do I need professional equipment to film one?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: "No. A phone camera, a quiet room with natural light facing you, and a stable surface to prop the phone on will beat a polished, over-produced video most of the time — donors respond to sincerity, not production value.",
                    },
                },
                {
                    '@type': 'Question',
                    name: 'Should I ask for a specific dollar amount in the video?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: "Yes — a vague ask gets a vague response. State the specific monthly amount you're inviting someone to consider, and pair it with what that number actually funds so it's concrete rather than abstract.",
                    },
                },
                {
                    '@type': 'Question',
                    name: 'Where should I send the video?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: "Attach or link it in your support letter and email rather than relying on a public social post alone — a direct message with a personal note and the video link converts better than a broadcast post, because it still feels like it's addressed to the one person watching it.",
                    },
                },
            ],
        },
    ],
};

export default function HowToMakeAMissionarySupportRaisingVideo() {
    usePageMeta(TITLE, DESCRIPTION, { path: '/resources/how-to-make-a-missionary-support-raising-video' });

    const articleRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const el = document.createElement('script');
        el.type = 'application/ld+json';
        el.setAttribute('data-support-raising-video-jsonld', 'true');
        el.textContent = JSON.stringify(JSON_LD);
        document.head.appendChild(el);
        return () => {
            el.remove();
        };
    }, []);

    return (
        <div className="min-h-screen bg-white flex flex-col">
            <HomeHeader />
            <div className="w-full h-64 md:h-80 bg-gray-100 overflow-hidden">
                <img src="/resources/cover24.jpg" alt="A missionary recording a short video appeal on a phone propped on a desk" className="w-full h-full object-cover" />
            </div>
            <div className="max-w-3xl mx-auto px-4 py-12 w-full">
                <h1 className="text-3xl font-bold mb-6">{TITLE}</h1>
                <TableOfContents targetRef={articleRef} />
                <div className="prose max-w-none" ref={articleRef}>
                    <p>A written support letter is still the backbone of most campaigns, but a short video often gets the first response a letter alone doesn't — people are more likely to watch 90 seconds of someone they know talk honestly about their calling than to read a full page of text. You don't need a production budget or any editing skill. You need a clear structure, decent lighting, and the same honesty that makes your letters work.</p>

                    <h2>The four-part structure that actually works</h2>

                    <p>Keep it simple and resist the urge to cover everything. A video that tries to explain your entire theology of missions in 90 seconds says nothing memorable. Four parts, in order:</p>

                    <ul>
                        <li><strong>Who you are and where you're going.</strong> Ten to fifteen seconds — your name, and the one-line version of your assignment. Don't assume the viewer already knows.</li>
                        <li><strong>Why this, why now.</strong> The real reason you're doing this, in your own words — not a mission-statement paraphrase. This is the part people actually remember.</li>
                        <li><strong>What the support funds, specifically.</strong> Not "the ministry" — a concrete picture: language school, a specific outreach, monthly field costs. Vague asks get vague responses.</li>
                        <li><strong>A direct, specific ask.</strong> Name the monthly amount you're inviting someone to consider and where to give — see <a href="/resources/how-to-write-a-fundraising-page-bio-that-gets-donations">how to write a fundraising page bio that gets donations</a> for the same clarity-over-cleverness principle applied to your written page.</li>
                    </ul>

                    <h2>Filming it with just a phone</h2>

                    <p>Face a window for natural light rather than having it behind you — backlighting turns you into a silhouette. Prop the phone at roughly eye level instead of holding it (a stack of books works fine), find a quiet room, and do a couple of practice takes before recording for real. You don't need a script memorized word for word; bullet points on a sticky note just out of frame keep you on track without sounding rehearsed. A slightly imperfect, sincere take beats a polished but stiff one almost every time.</p>

                    <h2>What to actually say — a loose script</h2>

                    <p>"Hi, I'm [name]. This [month/season] I'm heading to [place] to [one-line description of the work]. [One or two sentences on why this matters to you personally — the real reason, not the brochure reason.] To get there, I need to raise [$X] a month, which covers [one or two concrete costs]. If you'd consider joining as a monthly supporter at [$amount], it would mean a lot — the link's right here, and thank you for even watching this far." Adjust it to sound like you, not like a script being read.</p>

                    <h2>Where to send it — and how</h2>

                    <p>A video sitting alone on a public social post gets scrolled past. Attach it to a personal message or embed the link in your support letter and email — see our <a href="/resources/how-to-write-a-missionary-support-letter">guide to writing a missionary support letter</a> for how the two work together. A short personal note ("thought you'd want to hear this from me directly") plus the video link consistently outperforms a broadcast post, because it still reads as addressed to one person, not an audience.</p>

                    <h2>What not to do</h2>

                    <p>Don't over-produce it — background music, transitions, and text overlays add work without adding trust, and a video that looks like an ad gets treated like one. Don't make it longer than three minutes; if you need more time to explain your assignment, save that for the letter and keep the video as the hook. And don't skip the specific ask at the end — a heartfelt video that never actually invites the viewer to give leaves them moved but with nothing to do about it.</p>

                    <h2>Frequently asked questions</h2>

                    <p><strong>How long should a missionary support-raising video be?</strong><br />
                    90 seconds to 3 minutes — long enough to explain, short enough that people finish it.</p>

                    <p><strong>Do I need professional equipment?</strong><br />
                    No. A phone, natural light, and a quiet room outperform an over-produced video.</p>

                    <p><strong>Should I ask for a specific dollar amount?</strong><br />
                    Yes — a concrete monthly figure and what it funds gets a far better response than a vague ask.</p>

                    <p><strong>Where should I send the video?</strong><br />
                    In your support letter or a personal message, not just a public post — direct outreach converts better.</p>

                    <h2>Next reads on Devotion Fund</h2>
                    <ul>
                        <li><a href="/resources/how-to-write-a-missionary-support-letter">How to Write a Missionary Support Letter</a></li>
                        <li><a href="/resources/how-to-write-a-fundraising-page-bio-that-gets-donations">How to Write a Fundraising Page Bio That Gets People to Give</a></li>
                        <li><a href="/resources/how-to-ask-for-support-over-text-and-social-media">How to Ask for Support Over Text and Social Media</a></li>
                    </ul>

                    <h2>About the author</h2>
                    <p>We're the team behind <a href="/">Devotion Fund</a>. The missionaries on our platform who pair a short, honest video with their support page consistently tell us it's the piece that got a hesitant contact to actually respond.</p>
                    <p><small><em>Last updated: 2026-08-07.</em></small></p>
                </div>
                <div className="mt-8">
                    <EmailCapture page="/resources/how-to-make-a-missionary-support-raising-video" />
                </div>
            </div>
            <Footer />
        </div>
    );
}
